import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Card, CardBody, CardSubhead, CardTitle } from '@spark/ui-components/components/Card';
import JanelaDescargaHint, { CardTitleRow } from './JanelaDescargaHint';
import { formatTempo } from '../utils/formatters';

const pickFirstValue = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
};

const parseTempoToSeconds = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'object') {
    return parseTempoToSeconds(pickFirstValue(value, [
      'tempoEsperaMedio',
      'TempoEsperaMedio',
      'mediaTempoEspera',
      'MediaTempoEspera',
      'tempoMedioEspera',
      'TempoMedioEspera',
      'tempoMedio',
      'TempoMedio',
      'media',
      'Media',
      'valor',
      'Valor'
    ]));
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : null;
  }

  const normalized = String(value).trim().replace(/^H\s+/i, '');
  if (!normalized || normalized === '-') return null;

  const match = normalized.match(/^-?(?:(\d+)\.)?(\d+):(\d{2}):(\d{2})(?:\.\d+)?$/);

  if (match) {
    const isNegative = normalized.startsWith('-');
    if (isNegative) return 0;

    const days = Number(match[1] || 0);
    const hours = Number(match[2] || 0);
    const minutes = Number(match[3] || 0);
    const seconds = Number(match[4] || 0);

    return Math.max(0, (days * 86400) + (hours * 3600) + (minutes * 60) + seconds);
  }

  const numericValue = Number(normalized.replace(',', '.'));
  return Number.isFinite(numericValue) ? Math.max(0, Math.floor(numericValue)) : null;
};

const getClockAngles = (totalSeconds) => {
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600) % 12;

  return {
    hour: ((hours + (minutes / 60)) * 30) - 90,
    minute: ((minutes + (seconds / 60)) * 6) - 90,
    second: (seconds * 6) - 90
  };
};

const polarPoint = (center, radius, angleDegrees) => {
  const angle = (Math.PI / 180) * angleDegrees;
  return {
    x: center + (radius * Math.cos(angle)),
    y: center + (radius * Math.sin(angle))
  };
};

const MediaTempoEsperaCard = ({
  data,
  loading
}) => {
  const initialSeconds = useMemo(() => parseTempoToSeconds(data), [data]);
  const angles = getClockAngles(initialSeconds || 0);
  const hourPoint = polarPoint(100, 44, angles.hour);
  const minutePoint = polarPoint(100, 58, angles.minute);
  const secondPoint = polarPoint(100, 70, angles.second);

  return (
    <Card>
      <CardBody>
        <CardTitleRow>
          <CardTitle>Media de espera</CardTitle>
          <JanelaDescargaHint />
        </CardTitleRow>
        <CardSubhead>Tempo medio de permanencia na descarga</CardSubhead>

        <ClockArea>
          {loading ? (
            <EmptyState>Carregando metricas...</EmptyState>
          ) : initialSeconds === null ? (
            <EmptyState>Nenhuma metrica encontrada.</EmptyState>
          ) : (
            <>
              <ClockSvg viewBox="0 0 200 200" aria-hidden="true">
                <circle cx="100" cy="100" r="82" fill="none" stroke="#2ca6d9" strokeWidth="9" />
                <line x1="100" y1="18" x2="100" y2="34" stroke="#2ca6d9" strokeWidth="9" />
                <line x1="100" y1="166" x2="100" y2="182" stroke="#2ca6d9" strokeWidth="9" />
                <line x1="18" y1="100" x2="34" y2="100" stroke="#2ca6d9" strokeWidth="9" />
                <line x1="166" y1="100" x2="182" y2="100" stroke="#2ca6d9" strokeWidth="9" />
                <line x1="100" y1="100" x2={hourPoint.x} y2={hourPoint.y} stroke="#5f6872" strokeWidth="4" strokeLinecap="round" />
                <line x1="100" y1="100" x2={minutePoint.x} y2={minutePoint.y} stroke="#2ca6d9" strokeWidth="5" strokeLinecap="round" />
                <line x1="100" y1="100" x2={secondPoint.x} y2={secondPoint.y} stroke="#ff4d4f" strokeWidth="3" strokeLinecap="round" />
                <circle cx="100" cy="100" r="7" fill="#ff4d4f" />
              </ClockSvg>
              <TimeValue>{formatTempo(initialSeconds)}</TimeValue>
            </>
          )}
        </ClockArea>

        <LegendFooter>
          <LegendItem>
            <LegendDot />
            <span>Media de Tempo de Espera</span>
          </LegendItem>
        </LegendFooter>
      </CardBody>
    </Card>
  );
};

const ClockArea = styled.div`
  min-height: 270px;
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ClockSvg = styled.svg`
  width: min(190px, 100%);
  height: auto;
`;

const TimeValue = styled.div`
  margin-top: 8px;
  color: #087f98;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
`;

const LegendFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 18px -20px -20px;
  padding: 12px 20px 18px;
  border-top: 1px solid #d8dde3;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
`;

const LegendDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #2ca6d9;
  display: inline-block;
  flex: 0 0 14px;
`;

const EmptyState = styled.div`
  width: 100%;
  height: 270px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  background: #f8f9fa;
  border: 1px dashed #d8dde3;
`;

export default MediaTempoEsperaCard;
